"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { Dispatch, SetStateAction } from "react"
import { EngineGroup, FinishLevel, PackageOption } from "@eurodrive/utils"
import { LeasingCarDto, SaleCarDto } from "api"
import {
  Config,
  EngineOption,
  formatPrice,
  StepConfig,
} from "./CarConfiguration"

const StyledLayout = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  width: 100%;
  padding: 16px;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  border-radius: 8px 8px 0 0;
  background: #fff;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.16);
  z-index: 2;
  margin-top: auto;
  margin-left: -16px;
  margin-right: -16px;
  margin-bottom: -64px;
  width: calc(100% + 32px);
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 0;
    width: 100%;
    border-radius: 8px 8px 0 0;
  }
`

const PricesFlex = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
const TopBoxFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

function getNextStepButtonText(
  steps: StepConfig[],
  activeStep: number,
): string {
  const nextStep = steps[activeStep + 1]
  if (!nextStep) return "הבא"

  switch (nextStep.key) {
    case "trim":
      return "המשך לבחירת גימור"
    case "color":
      return "המשך לבחירת צבע"
    case "finish":
      return "המשך לבחירת גימור"
    case "packages":
      return "המשך לבחירת חבילות"
    case "summary":
      return "המשך לסיכום"
    default:
      return "הבא"
  }
}

export default function StickyFooter({
  config,
  options,
  engineGroups = [],
  activeStep,
  setActiveStep,
  completed,
  steps,
  colors,
  finishLevels,
  additionalPackages,
  isLeasing,
  data,
}: {
  config: Config
  options: EngineOption[]
  engineGroups?: EngineGroup[]
  activeStep: number
  setActiveStep: Dispatch<SetStateAction<number>>
  completed: boolean[]
  steps: StepConfig[]
  colors: { color: string; label: string; price: number }[]
  finishLevels: FinishLevel[]
  additionalPackages: PackageOption[]
  isLeasing: boolean
  data: SaleCarDto | LeasingCarDto
}) {
  // Calculate running total price
  const colorPrice =
    config.color !== null ? colors[config.color]?.price || 0 : 0
  const finishLevelPrice =
    isLeasing && config.finishLevel !== null && config.finishLevel !== undefined
      ? finishLevels[config.finishLevel]?.monthlyPrice || 0
      : 0
  const packagesPrice =
    isLeasing && config.packages !== null && config.packages !== undefined
      ? additionalPackages[config.packages]?.monthlyPrice || 0
      : 0

  // For sales with engine groups, get price from selected trim
  const selectedEngineGroup =
    !isLeasing && engineGroups.length > 0 && config.engine !== null
      ? engineGroups[config.engine]
      : null
  const selectedTrim =
    selectedEngineGroup && config.trim !== null
      ? selectedEngineGroup.trims[config.trim]
      : null

  // Use advertisedPrice from car data as fallback when no engine is selected
  const baseAdvertisedPrice = !isLeasing
    ? Number((data as SaleCarDto).advertisedPrice) || 0
    : 0
  const enginePrice = selectedTrim
    ? selectedTrim.price
    : config.engine !== null
      ? options[config.engine].price
      : baseAdvertisedPrice
  const totalPrice = colorPrice + enginePrice + finishLevelPrice + packagesPrice

  // Calculate old price if applicable
  const engineOldPrice = selectedTrim
    ? selectedTrim.oldPrice
    : config.engine !== null
      ? options[config.engine].oldPrice
      : null
  const oldTotalPrice = engineOldPrice
    ? colorPrice + engineOldPrice + finishLevelPrice + packagesPrice
    : null
  // Hide footer on the last step (summary)
  if (activeStep === steps.length - 1) {
    return null
  }

  const activeStepKey = steps[activeStep]?.key
  const hasValidPackageSelection =
    config.packages !== null &&
    config.packages !== undefined &&
    Boolean(additionalPackages[config.packages])
  const canContinue =
    activeStepKey === "packages"
      ? hasValidPackageSelection
      : completed[activeStep]

  // Only show price after engine has been selected
  const hasEngineSelected = config.engine !== null

  return (
    <StyledLayout>
      {hasEngineSelected && (
        <TopBoxFooter>
          <Typography variant="body1" component={"h2"}>
            סה״כ לתשלום
          </Typography>
          <PricesFlex>
            {oldTotalPrice && (
              <Typography
                variant="body3"
                component={"p"}
                style={{ textDecoration: "line-through" }}
                color="black"
              >
                {formatPrice(oldTotalPrice)}
              </Typography>
            )}
            <Typography variant="h4" fontWeight={700} component={"p"}>
              {formatPrice(totalPrice)}
            </Typography>
          </PricesFlex>
        </TopBoxFooter>
      )}
      <Button
        size="large"
        variant="primary"
        fullWidth
        disabled={!canContinue}
        onClick={() => {
          if (!canContinue) return
          const next = Math.min(activeStep + 1, steps.length - 1)
          setActiveStep(next)
        }}
      >
        {getNextStepButtonText(steps, activeStep)}
      </Button>
    </StyledLayout>
  )
}
